import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { api_routes, db_tables, features, routes } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { NotificationService } from '@app/core/notifications/notification.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { BuySellApproval, BuySellPlanEmployee } from '../models/buysell.model';
import { BuySellService } from '../services/buy-sell.service';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { merge } from 'rxjs';
import { EmployeeBuySellRequestComponent } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-time-off/components/employee-buy-sell-request/employee-buy-sell-request.component';

@Component({
  selector: 'app-buy-sell-approvals',
  templateUrl: './buy-sell-approvals.component.html',
  styleUrls: ['./buy-sell-approvals.component.scss']
})
export class BuySellApprovalsComponent implements OnInit {
  public columns: any[] = [
    { field: "employee", title: "Employee", type: "employee", localizationCode: 'Employee' },
    { field: "plan", subField: "name", title: "Plan Name", localizationCode: 'BuySellPlanName' },
    { field: "status", subField: "text", title: "Status", type: 'chip', chipValue: ['Complete', 'Denied', 'Pending Approval', 'Queued'], localizationCode: 'BuySellStatus' },
    { field: "isApproved", title: "Is Approved", type: 'chip', chipValue: [true, false, '', ''], chipBooleanText: ['Approved', 'Denied'], localizationCode: 'BuySellIsApproved' },
    { field: "reviewer", title: "Reviewer", type: "employee", localizationCode: 'BuySellReviewer' },
    { field: "orderNumber", title: "Order Number", localizationCode: 'BuySellOrderNumber' },
    // { field: "version", subField: "createdBy", title: "Created By", type: "employee" },
    // { field: "version", subField: "createdOn", title: "Created On", type: "date" },
  ];

  filterCategories: any[] = [
    { field: "employee.firstName", title: "Employee First Name", type: "String", dataType: 'String' },
    { field: "employee.lastName", title: "Employee Last Name", type: "String", dataType: 'String' },
    { field: "status.text", title: "Status", type: "String", dataType: 'String' },
    { field: "isApproved", title: "Is Approved", type: "Boolean", dataType: 'Boolean' },
    { field: "plan.name", title: "Plan", type: "String", dataType: 'String' },
    { field: "reviewer.firstName", title: "Reviewer First Name", type: "String", dataType: 'String' },
    { field: "reviewer.lastName", title: "Reviewer Last Name", type: "String", dataType: 'String' },
    { field: "orderNumber", title: "Order Number", type: "Number", dataType: 'Number' },
    // { field: "version.createdOn", title: "Created On", type: "Date", dataType: 'Date' },
  ];

  sortableColumns: any[] = [
    { field: "employee", sortValue: ["employee.firstName", "employee.lastName"] },
    { field: "status", sortValue: "status.text" },
    { field: "isApproved", sortValue: "isApproved" },
    { field: "plan", sortValue: "plan.name" },
    { field: "reviewer", sortValue: ["reviewer.firstName", "reviewer.lastName"] },
    { field: "orderNumber", sortValue: "orderNumber" },
    // { field: "version", sortValue: "version.createdOn" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string = 'version.createdOn-desc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string = 'buySellApprovals';
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  user$: any;

  constructor(
    public translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    private overlay: Overlay,
    private buySellService: BuySellService,
    private overlayService: OverlayService,
    private oidcAuthService: OidcAuthService,
    private featureService: FeatureService,
    private notificationService: NotificationService,
    private destroyRef: DestroyRef
  ) {
    this.user$ = this.oidcAuthService.userProfile;
  }

  ngOnInit(): void {
    this.getBuySellApprovals();

    merge(
      this.notificationService.on('buysell.request.reviewpending'),
      this.notificationService.on('buysell.request.approved'),
      this.notificationService.on('buysell.request.denied')
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.getRequest?.unsubscribe();
        this.getBuySellApprovals();
      });
  }

  getBuySellApprovals() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let baseFilter = ``;
    // If the user is not an admin, filter to only show their own approvals
    if (!this.featureService.hasFeatureAccess(features.BUY_SELL_ADMIN)) {
      baseFilter = `((employee.id = "${this.user$.userId}") OR (reviewer.id = "${this.user$.userId}"))`;
    }

    // Build combinedFilter only including non-blank parts
    const parts: string[] = [];
    if (baseFilter && baseFilter.trim().length > 0) {
      parts.push(`(${baseFilter})`);
    }
    if (this.filterString && this.filterString.trim().length > 0) {
      parts.push(`(${this.filterString})`);
    }
    if (this.searchFilterString && this.searchFilterString.trim().length > 0) {
      parts.push(`(${this.searchFilterString})`);
    }

    const combinedFilter = parts.length > 0 ? parts.join(' AND ') : '';
    // Use combinedFilter in the service call

    this.getRequest = this.buySellService.getBuySellApprovals(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  openBuySellRequest(buySellApproval: BuySellApproval) {
    this.overlayService.show('Loading');

    // Combine filterString and searchFilterString if both exist
    let baseFilter = `(employee.id = "${buySellApproval?.employee?.id}") AND (plan.id = "${buySellApproval?.plan?.id}")`;

    this.buySellService.getBuySellPlanEmployees(20, 0, baseFilter).subscribe({
      next: (data) => {
        this.overlayService.hide();
        if (data.data.length > 0) {
          this.openDialog(data.data[0], buySellApproval?.reviewer?.id === this.user$.userId);
        }
      },
      error: (error) => {
        this.overlayService.hide();
      }
    });
  }

  openDialog(buySellPlanEmployee: BuySellPlanEmployee, isReviewer: boolean) {
    let dialogRef;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.scrollStrategy = this.overlay.scrollStrategies.block();

    dialogConfig.data = {
      employeeId: buySellPlanEmployee.employee.id,
      buySellPlanEmpId: buySellPlanEmployee.id,
      isReviewer: isReviewer
    };

    dialogRef = this.dialog.open(EmployeeBuySellRequestComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.getBuySellApprovals();
        }
      }
    );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Employee.FirstName like "${variant}") OR (Employee.LastName like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBuySellApprovals();
  }
}
